<template>
  <div class="assistant-popover-container">
    <AssistantStartButton  @click="toggle"/>

    <Dialog v-model:visible="visibleHelpModal" dismissableMask modal :header="modalHeader"
            class="assistant-modal"
            :style="{ width: '75rem' }"
            :breakpoints="{ '1199px': '75vw', '575px': '95vw' }">
      <HelpAssistantModal v-if="currentModalView === ASSISTANT_MODAL_SCREENS.HELP" @to-catalog="handleCatalogClick"/>
      <CatalogModalContent v-else-if="currentModalView === ASSISTANT_MODAL_SCREENS.CATALOG"
                           @create="handleAssistantToCreating"
                           @attach="attachAssistant"
                           @detach="detachAssistant"
                           @view="handleAssistantToPreview"/>
      <EditorContent v-else-if="currentModalView === ASSISTANT_MODAL_SCREENS.EDITOR"
                     :assistant="editingAssistant"
                     :modalState="assistantModelState"
                     @to-catalog="handleCatalogClick"
                     @delete="handleDeleteAssistant"
                     @edit="handleAssistantToEditing"
                     @save="handleSaveAssistant"/>
    </Dialog>

    <Popover ref="visiblePopover" class="assistant-popover-wrapper" :dismissable="dismissiblePopover">
      <div class="assistant-popover">
        <div class="assistant-popover__header">
          <div>
            Ассистенты решают конкретную задачу.
          </div>
        </div>
        <SelectedAssistants
            v-if="dialogAssistants.length"
            :assistants="dialogAssistants"
            @detach="detachAssistant"
            @reorder="handleReorder"
            @view="handleAssistantToPreview"
        />
        <hr v-else>

        <AvailableAssistantRows
            :assistants="listAssistants"
            @attach="attachAssistant"
            @view="handleAssistantToPreview"
        />

        <div class="assistant-popover__info">
          <div>
            Создавайте своих ассистентов или используйте из каталога.
          </div>
          <div>
            Для добавления ассистента в диалог нажмите на <i class="pi pi-plus"></i><br><br>Пример цепочки ассистентов:<br>Критик / Редактор / Переводчик.
          </div>
        </div>

        <div class="assistant-popover__actions">
          <Button type="button" severity="secondary" icon="pi pi-home" @click="handleCatalogClick" label="Каталог"/>
          <Button v-if="useAppStateStore().features.manageAssistants" type="button" severity="secondary" icon="pi pi-th-large" @click="handleAssistantToCreating" label="Создать"/>
          <Button type="button" severity="secondary" label="Помощь" @click="handleHelpClick" icon="pi pi-lightbulb"
                  title="Помощь"/>
        </div>

        <Button type="button" variant="text" rounded icon="pi pi-times" size="small" severity="secondary"
                class="assistant-popover__close-button"
                @click="toggle"/>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import {computed, ref} from "vue";
import Popover from "primevue/popover";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import {useReferenceStore} from "@/modules/chat/stores/referenceStore";
import {useChatStore} from "@/modules/chat/stores/chatStore";
import HelpAssistantModal from "@/modules/chat/components/assisstant/Help/HelpAssistantModal.vue";
import SelectedAssistants from "@/modules/chat/components/assisstant/Popover/SelectedAssistants.vue";
import AvailableAssistantRows from "@/modules/chat/components/assisstant/Popover/AvailableAssistantRows.vue";
import AssistantStartButton from "@/modules/chat/components/assisstant/Popover/AssistantStartButton.vue";
import CatalogModalContent from "@/modules/chat/components/assisstant/Catalog/CatalogModalContent.vue";
import {ASSISTANT_MODAL_SCREENS, ASSISTANT_MODAL_STATES} from "@/constants";
import EditorContent from "@/modules/chat/components/assisstant/Editor/EditorContent.vue";
import {useConfirm} from "primevue/useconfirm";
import {useAppStateStore} from "@/modules/chat/stores/appStore";

const reference = useReferenceStore();
const chatState = useChatStore();

const visibleHelpModal = ref(false);
const visiblePopover = ref(false);
const currentModalView = ref(ASSISTANT_MODAL_SCREENS.HELP);
const assistantModelState = ref(ASSISTANT_MODAL_STATES.CREATING);

const editingAssistant = ref(null);

const toggle = (event) => {
  visiblePopover.value.toggle(event);
};

const repositionPopover = () => {
  visiblePopover.value?.alignOverlay();
};

const dismissiblePopover = computed(() => !visibleHelpModal.value);

const listAssistants = computed(() => {
  const dialogAssistants = chatState.assistants;
  const remainingSlots = 5 - dialogAssistants.length;

  if (remainingSlots <= 0) return [];

  return reference.quick_assistants
      .filter((quickAssistantId) => {
        const dialogAssistant = dialogAssistants.find(a => a.assistant_id === quickAssistantId);
        return !dialogAssistant;
      })
      .map(quickAssistantId => reference.assistants.find(assistant => assistant.id === quickAssistantId))
      .filter(Boolean) // На случай, если какой-то ассистент не найден
      .slice(0, remainingSlots);
});

const dialogAssistants = computed(() => {
  const result = chatState.assistants.map(dialogAssistant => {
    const assistantId = dialogAssistant.assistant_id;
    const assistantInfo = reference.assistants.find(assistant => assistantId === assistant.id);
    return {...dialogAssistant, assistantInfo};
  });
  return result.sort((a, b) => a.order - b.order);
});

const modalHeader = computed(() => {
  let result = 'Кто такие ассистенты?';
  if (currentModalView.value === ASSISTANT_MODAL_SCREENS.CATALOG) {
    result = 'Каталог ассистентов';
  } else if (currentModalView.value === ASSISTANT_MODAL_SCREENS.EDITOR) {
    if (assistantModelState.value === ASSISTANT_MODAL_STATES.EDITING) {
      result = 'Редактирование ассистента';
    } else if (assistantModelState.value === ASSISTANT_MODAL_STATES.CREATING) {
      result = 'Создание ассистента';
    } else {
      result = 'Просмотр ассистента';
    }
  }
  return result;
});

const attachAssistant = async (assistantId) => {
  await chatState.attachAssistant(assistantId);
  repositionPopover();
};

const detachAssistant = async (assistantId) => {
  await chatState.detachAssistant(assistantId);
  repositionPopover();
};

const handleHelpClick = () => {
  currentModalView.value = ASSISTANT_MODAL_SCREENS.HELP;

  visibleHelpModal.value = true;
};

const handleAssistantToCreating = () => {
  editingAssistant.value = null;
  currentModalView.value = ASSISTANT_MODAL_SCREENS.EDITOR;
  assistantModelState.value = ASSISTANT_MODAL_STATES.CREATING;

  visibleHelpModal.value = true;
};

const handleAssistantToEditing = (assistant) => {
  editingAssistant.value = assistant;
  currentModalView.value = ASSISTANT_MODAL_SCREENS.EDITOR;
  assistantModelState.value = ASSISTANT_MODAL_STATES.EDITING;

  visibleHelpModal.value = true;
};

const handleAssistantToPreview = (assistant) => {
  editingAssistant.value = assistant;
  currentModalView.value = ASSISTANT_MODAL_SCREENS.EDITOR;
  assistantModelState.value = ASSISTANT_MODAL_STATES.PREVIEW;

  visibleHelpModal.value = true;
};

const handleCatalogClick = () => {
  currentModalView.value = ASSISTANT_MODAL_SCREENS.CATALOG;

  visibleHelpModal.value = true;
};

const handleSaveAssistant = async (assistantData) => {
  editingAssistant.value = editingAssistant.value?.id
      ? await chatState.updateAssistant(editingAssistant.value.id, assistantData)
      : await chatState.createAssistant(assistantData);
  assistantModelState.value = ASSISTANT_MODAL_STATES.PREVIEW;
};

const confirm = useConfirm();
const handleDeleteAssistant = async (id) => {
  confirm.require({
    message: 'Подтвердите удаление. Операция необратима. Ассистент открепится от всех диалогов.',
    header: 'Удаление ассистента',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Отмена',
    rejectProps: {
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Удалить',
      severity: 'danger'
    },
    accept: async () => {
        await chatState.deleteAssistant(id);
        currentModalView.value = ASSISTANT_MODAL_SCREENS.CATALOG;
    },
  });
};

const handleReorder = async (event) => {
  const reorderedAssistants = dialogAssistants.value.map((assistant, index) => ({
    assistant_id: assistant.assistant_id,
    order: index,
  }));
  await chatState.updateAssistantsOrder(reorderedAssistants);
};
</script>